/**
 * useRefetchOnReviseComplete — fire a per-step `refetch()` when the
 * step's status transitions from `revising` back to `draft` (or
 * `approved`, if the agent's re-run was also auto-approved upstream).
 *
 * Why this exists: the reducer's `state.steps[N].result` slot is
 * always fresh because `HYDRATE_FROM_BACKEND` rewrites it on every
 * polling tick. But each per-step hook also runs a separate
 * `useStructuredFetch` against `getAgentResult` /
 * `getItemDefinition` etc. to drive the visible table — and that
 * fetch's `enabled` flag stays `true` across the draft→revising→draft
 * arc (all three are in HAS_OUTPUT_STATUSES). Without an explicit
 * trigger the structured data stays stuck at the pre-revise snapshot
 * even after polling has updated the reducer.
 *
 * One line in each step hook closes the gap:
 *   useRefetchOnReviseComplete(stepState?.status ?? 'empty', structured.refetch);
 *
 * Mirrors the legacy behavior at AthenaComponents/ReviewPanel.jsx:548
 * (post-revise, fetch latest agent result + write into per-step setters).
 */
import { useEffect, useRef } from 'react';
import type { StepStatus } from '../state/workflowV2Types';

export function useRefetchOnReviseComplete(
  status: StepStatus,
  refetch: () => void,
): void {
  const prev = useRef(status);
  useEffect(() => {
    if (
      prev.current === 'revising' &&
      (status === 'draft' || status === 'approved')
    ) {
      refetch();
    }
    prev.current = status;
  }, [status, refetch]);
}
